import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { HandCoins, Users, Wallet, CheckCircle, RefreshCw, MapPin, Phone, Receipt, Search, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const rp = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

const monthOptions = (() => {
  const opts = [];
  const now = new Date();
  for (let i = -3; i <= 1; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const kode = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ kode, label: d.toLocaleDateString("id-ID", { month: "long", year: "numeric" }) });
  }
  return opts;
})();

export const PenarikDashboardPage = () => {
  const currentKode = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`;
  const [periode, setPeriode] = useState(currentKode);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notif, setNotif] = useState("");
  const [wilayahFilter, setWilayahFilter] = useState("ALL");
  const [rtFilter, setRtFilter] = useState("ALL");
  const [rwFilter, setRwFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true); setError("");
      const res = await axios.get(`${BACKEND_URL}/api/penarik/dashboard?periode=${periode}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal memuat data");
    } finally { setLoading(false); }
  }, [periode]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const bayar = async (id, nama) => {
    if (!window.confirm(`Terima pembayaran tunai dari ${nama}?`)) return;
    try {
      await axios.post(`${BACKEND_URL}/api/penarik/tagihan/${id}/bayar`, {}, { withCredentials: true });
      setNotif(`Pembayaran ${nama} berhasil dicatat!`); setTimeout(() => setNotif(""), 4000);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || "Gagal mencatat pembayaran"); }
  };

  const s = data?.summary || {};
  const rows = data?.tagihan || [];
  const wilayahOptions = [...new Set(rows.map((t) => t.wilayah).filter(Boolean))];
  const rtOptions = [...new Set(rows.map((t) => t.rt).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  const rwOptions = [...new Set(rows.map((t) => t.rw).filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  const q = search.trim().toLowerCase();
  const filteredRows = rows.filter((t) =>
    (!q || (t.pelanggan_nama || "").toLowerCase().includes(q)) &&
    (wilayahFilter === "ALL" || t.wilayah === wilayahFilter) &&
    (rtFilter === "ALL" || t.rt === rtFilter) &&
    (rwFilter === "ALL" || t.rw === rwFilter) &&
    (statusFilter === "ALL" || (statusFilter === "PAID" ? t.status === "PAID" : t.status !== "PAID"))
  );

  return (
    <div className="space-y-6" data-testid="penarik-dashboard">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] border border-slate-800 shadow-xl">
        <div>
          <span className="px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 text-[10px] font-bold uppercase tracking-wider">Collector Panel</span>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">Dashboard Penarik — {data?.penarik?.nama || ""}</h1>
          <p className="text-xs text-slate-400 mt-0.5">Wilayah: {(data?.penarik?.wilayah || []).join(", ") || "-"} · Honor/pelanggan: {rp(data?.penarik?.honor_per_pelanggan)}</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={periode} onChange={(e) => setPeriode(e.target.value)} data-testid="penarik-periode" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold">
            {monthOptions.map((o) => <option key={o.kode} value={o.kode}>{o.label}</option>)}
          </select>
          <button onClick={fetchData} data-testid="penarik-refresh-btn" className="px-3 py-2 bg-slate-800/80 hover:bg-slate-700 text-slate-200 text-xs font-semibold rounded-xl border border-slate-700 flex items-center gap-1.5"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />Refresh</button>
        </div>
      </div>

      {notif && <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-xl text-xs flex items-center gap-2"><CheckCircle className="w-4 h-4" /> {notif}</div>}
      {error && <div className="p-3 bg-rose-500/10 border border-rose-500/40 text-rose-300 rounded-xl text-xs" data-testid="penarik-error">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-lg" data-testid="penarik-card-aktif">
          <div className="flex justify-between items-start"><div><p className="text-xs font-semibold text-slate-400">Pelanggan Aktif</p><h3 className="text-2xl font-black text-white mt-1">{s.jumlah_aktif || 0}</h3><p className="text-[10px] text-slate-500 mt-1">dari {s.jumlah_pelanggan || 0} pelanggan</p></div><div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 text-cyan-400 flex items-center justify-center"><Users className="w-5 h-5" /></div></div>
        </div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-lg" data-testid="penarik-card-honor">
          <div className="flex justify-between items-start"><div><p className="text-xs font-semibold text-slate-400">Honor Bulan Ini</p><h3 className="text-lg font-black text-emerald-400 mt-1 font-mono">{rp(s.honor_total)}</h3><p className="text-[10px] text-slate-500 mt-1">{s.jumlah_aktif || 0} × {rp(data?.penarik?.honor_per_pelanggan)}</p></div><div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center"><HandCoins className="w-5 h-5" /></div></div>
        </div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-lg" data-testid="penarik-card-lunas">
          <div className="flex justify-between items-start"><div><p className="text-xs font-semibold text-slate-400">Sudah Tertagih</p><h3 className="text-lg font-black text-emerald-400 mt-1 font-mono">{rp(s.total_lunas)}</h3><p className="text-[10px] text-slate-500 mt-1">{s.lunas_count || 0} tagihan lunas</p></div><div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center"><Wallet className="w-5 h-5" /></div></div>
        </div>
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-lg" data-testid="penarik-card-unpaid">
          <div className="flex justify-between items-start"><div><p className="text-xs font-semibold text-slate-400">Belum Tertagih</p><h3 className="text-lg font-black text-rose-400 mt-1 font-mono">{rp(s.total_unpaid)}</h3><p className="text-[10px] text-slate-500 mt-1">{s.unpaid_count || 0} tagihan</p></div><div className="w-10 h-10 rounded-xl bg-rose-500/15 border border-rose-500/30 text-rose-400 flex items-center justify-center"><Receipt className="w-5 h-5" /></div></div>
        </div>
      </div>

      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl space-y-3">
        <h2 className="text-sm font-bold text-white border-b border-slate-800 pb-3 flex items-center gap-2"><Receipt className="w-4 h-4 text-amber-400" />Tagihan Wilayah Anda ({filteredRows.length}{filteredRows.length !== rows.length ? ` dari ${rows.length}` : ""})</h2>

        <div className="relative">
          <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama pelanggan..." data-testid="penarik-search-nama" className="w-full pl-9 pr-9 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-amber-500" />
          {search && <button onClick={() => setSearch("")} data-testid="penarik-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>}
        </div>

        {/* Filter: Wilayah, RT, RW, Status */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="penarik-filter-bar">
          <select value={wilayahFilter} onChange={(e) => setWilayahFilter(e.target.value)} data-testid="penarik-filter-wilayah" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200">
            <option value="ALL">Semua Wilayah</option>
            {wilayahOptions.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          <select value={rtFilter} onChange={(e) => setRtFilter(e.target.value)} data-testid="penarik-filter-rt" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200">
            <option value="ALL">Semua RT</option>
            {rtOptions.map((r) => <option key={r} value={r}>RT {r}</option>)}
          </select>
          <select value={rwFilter} onChange={(e) => setRwFilter(e.target.value)} data-testid="penarik-filter-rw" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200">
            <option value="ALL">Semua RW</option>
            {rwOptions.map((r) => <option key={r} value={r}>RW {r}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="penarik-filter-status" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200">
            <option value="ALL">Semua Status</option>
            <option value="PAID">Sudah Bayar</option>
            <option value="UNPAID">Belum Bayar</option>
          </select>
        </div>
        <div className="space-y-2.5">
          {loading && <p className="text-xs text-slate-500 py-3">Memuat...</p>}
          {!loading && filteredRows.length === 0 && <p className="text-xs text-slate-500 italic py-3" data-testid="penarik-empty-tagihan">Tidak ada tagihan yang cocok dengan filter pada periode ini.</p>}
          {filteredRows.map((t) => (
            <div key={t.id} className="p-3.5 rounded-xl bg-slate-900/70 border border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`penarik-tagihan-${t.id}`}>
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-white text-xs">{t.pelanggan_nama}</span>
                  <span className="text-[10px] font-mono text-slate-400">{t.no_invoice}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${t.status === "PAID" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>{t.status === "PAID" ? "LUNAS" : "BELUM BAYAR"}</span>
                </div>
                <p className="text-[11px] text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3 text-purple-400" />{t.pelanggan_alamat}{(t.rt || t.rw) ? ` (RT ${t.rt || "-"}/RW ${t.rw || "-"})` : ""} — {t.wilayah}</p>
                <p className="text-sm font-black text-cyan-400 font-mono">{rp(t.total_tagihan)}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {t.pelanggan_no_hp && (
                  <a href={`https://wa.me/${String(t.pelanggan_no_hp).replace(/\D/g, "").replace(/^0/, "62")}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/40 border border-emerald-600/40 text-emerald-300 text-[11px] font-bold rounded-xl flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {t.pelanggan_no_hp}</a>
                )}
                {t.status !== "PAID" && (
                  <button onClick={() => bayar(t.id, t.pelanggan_nama)} data-testid={`penarik-bayar-${t.id}`} className="px-3 py-1.5 bg-orange-600 hover:bg-orange-500 text-white text-[11px] font-bold rounded-xl">Terima Tunai</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
